import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_DISC,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";

import { getDiscPersonalityTopTwoTraitsByScore } from "../../utils/assessment-utils";

import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import SendToFriendButton from "../../components/send-to-friend-button";
import ShareButton from "../../components/share-button";
import {
  getCookie,
  getDiSCImage,
  getEnneagramBannerImage,
} from "../../utils/utility-functions";

import ExpandableSection from "../../components/expandable-collapsible/component";

import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";
import { CallToActionSection } from "../../components/call-to-action/component";
import InviteFriend from "../../components/invite-friend";
import ReportWrapper from "../../components/report-wrapper";

const DiscPersonalityReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,

  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,
  getGuestUserConnect,
  getOrderDataByUserIdConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  const { t } = useTranslation();
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_DISC[currentLocale] || MODEL_TYPE_DISC[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;

          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  // function getTopTwoTraitsByScore(data) {
  //   data?.sort((a, b) => b.score - a.score);
  //   return data.slice(0, 2).map((item) => item.trait.title);
  // }

  const topTwoTraits =
    getDiscPersonalityTopTwoTraitsByScore(userTraits).join("_");

  const data = contentData[`${topTwoTraits}`];

  const strengthsData = data?.strengths;

  const challengesData = data?.challenges;

  const interactionsDecisionsData = data?.interactionsDecisions;

  const tipsProfDevData = data?.tipsProfDev;

  const tipsPersonalGrowthRelationshipsData =
    data?.tipsPersonalGrowthRelationships;

  const otherDimensionsData = data?.otherDimensions;

  const CTA_1 = contentData?.CTA_1;

  const CTA_2 = contentData?.CTA_2;

  const bannerImage = getEnneagramBannerImage();
  const result1ImageText = "disc-report-1";
  const result2ImageText = "disc-report-2";
  const result3ImageText = "disc-report-3";
  const result1Image = getDiSCImage("disc-report-1", currentLocale);
  const result2Image = getDiSCImage(result2ImageText);
  const result3Image = getDiSCImage(result3ImageText);

  const topTwoTraitsDummy = getDiscPersonalityTopTwoTraitsByScore(userTraits);

  const getFilteredTraits = (topTwoTraitsDummy) => {
    const traitsArray = [
      "conscientiousness",
      "influence",
      "dominance",
      "steadiness",
    ];

    return traitsArray.filter(
      (trait) =>
        !topTwoTraitsDummy.some((letter) =>
          trait.startsWith(letter.toLowerCase())
        )
    );
  };

  const otherDimensionsArray = getFilteredTraits(topTwoTraitsDummy);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.career_preference_precontent.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center" id={reportType}>
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12" id={reportType}>
            {/* Title & Button Wrapper */}
            <div
              className="flex flex-col  md:flex-row items-center md:items-start justify-between gap-4 pr-10 md:pr-16"
              id={reportType}
            >
              {/* Title */}
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl md:text-4xl font-bold text-white  break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const InfoListCard = ({ heading, bgClr, contentArray, overview }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full`}
      >
        {heading && (
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            {heading}
          </h2>
        )}
        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2">
            {contentArray?.map((item, index) => {
              const [title, ...descParts] = item.split(": ");
              const description = descParts.join(": ");

              return (
                <li key={index} className="flex gap-4">
                  <div className="w-8 h-8 flex">
                    <svg
                      width="100%"
                      height="100%"
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <filter
                          id="shadow"
                          x="-20%"
                          y="-20%"
                          width="140%"
                          height="140%"
                        >
                          <feDropShadow
                            dx="0"
                            dy="2"
                            stdDeviation="3"
                            floodColor="black"
                            floodOpacity="0.4"
                          />
                        </filter>
                      </defs>
                      <path
                        d="M24 33C28.9706 33 33 28.9706 33 24C33 19.0294 28.9706 15 24 15C19.0294 15 15 19.0294 15 24C15 28.9706 19.0294 33 24 33Z"
                        fill="#805fe6"
                        stroke="#fff"
                        strokeWidth="6"
                        filter="url(#shadow)"
                      />
                    </svg>
                  </div>

                  <p className="flex-1">
                    <strong className="text-[#805fe6]">{title}:</strong>{" "}
                    {description}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          filter={`url(#shadow-${character || "bullet"}-${actualSize})`}
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const CallToActionSectionForUnAuth = ({
    onClick1,
    onClick2,
    CTA_1,
    CTA_2,
  }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {CTA_1}
            </h3>

            <TakeTestButton
              onClick={onClick1}
              id="disc-personality-report-personality-test-button"
            />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {CTA_2}
            </h3>

            <TakeTestButton
              onClick={onClick2}
              id="disc-personality-report-big-five-test-button"
            />
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData?.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-8 items-center">
                      {/* Text Section (Wider) */}
                      <div className="flex-1">
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                          {contentData?.overview}
                        </p>
                      </div>

                      {/* Image Section (Smaller) */}
                      <div className="flex items-center justify-center">
                        <img
                          src={result1Image}
                          alt="Banner Image 1 "
                          className="w-full max-w-[350px] md:max-w-[400px] object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={data?.your_Score?.Heading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                      {/* Text Section */}
                      <div className="flex-1">
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                          {data?.your_Score?.Content}
                        </p>
                      </div>

                      {/* Image Section */}
                      <div className="flex items-center justify-center">
                        <img
                          src={result2Image}
                          alt="Banner Image 1"
                          className="w-full max-w-[400px] object-contain"
                        />
                      </div>
                    </div>

                    {/* Cards Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      {/* Primary Section */}
                      <DescriptiveCard
                        heading={data?.primary_heading}
                        bgClr="bg-blue-50"
                        textClr="text-blue-800"
                        overview={data?.primary_content}
                      />

                      {/* Secondary Section */}
                      <DescriptiveCard
                        heading={data?.secondary_heading}
                        bgClr="bg-purple-50"
                        textClr="text-purple-800"
                        overview={data?.secondary_content}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t(
                        "report.disc_personality.strengths_and_challenges"
                      )}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      {/* Strengths Card */}
                      <div className="flex flex-col h-full">
                        <InfoListCard
                          heading={strengthsData?.Heading}
                          contentArray={strengthsData?.Content}
                          bgClr={"#fcf6ec"}
                        />
                      </div>

                      {/* Challenges Card */}
                      <div className="flex flex-col h-full">
                        <InfoListCard
                          heading={challengesData?.Heading}
                          contentArray={challengesData?.Content}
                          bgClr={"#c7edf9"}
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={interactionsDecisionsData?.Heading}
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-8 mb-8 items-center">
                      {/* Text Section */}
                      <div className="flex-1">
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                          {interactionsDecisionsData?.Content}
                        </p>
                      </div>

                      {/* Image Section */}
                      <div className="flex items-center justify-center">
                        <img
                          src={result3Image}
                          alt="Banner Image 1"
                          className="w-full max-w-[400px] object-contain"
                        />
                      </div>
                    </div>

                    <h2 className="text-3xl font-semibold text-gray-700 mb-4">
                      {interactionsDecisionsData?.Heading}
                    </h2>
                    <InfoListCard
                      overview={tipsProfDevData?.FlavorText}
                      contentArray={tipsProfDevData?.Content}
                      bgClr={"#eae2fd"}
                    />
                    <h2 className="text-3xl font-semibold text-gray-700 my-4 mt-8">
                      {tipsPersonalGrowthRelationshipsData?.Heading}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      {/* Building Strong Relationships */}
                      <div className="flex flex-col h-full">
                        <InfoListCard
                          heading={
                            tipsPersonalGrowthRelationshipsData
                              ?.buildingStrongRelationships?.Heading
                          }
                          contentArray={
                            tipsPersonalGrowthRelationshipsData
                              ?.buildingStrongRelationships?.Content
                          }
                          overview={
                            tipsPersonalGrowthRelationshipsData
                              ?.buildingStrongRelationships?.FlavorText
                          }
                          bgClr={"#eff4fd"}
                        />
                      </div>

                      {/* Growing with Others */}
                      <div className="flex flex-col h-full">
                        <InfoListCard
                          heading={
                            tipsPersonalGrowthRelationshipsData
                              ?.growingWithOthers?.Heading
                          }
                          contentArray={
                            tipsPersonalGrowthRelationshipsData
                              ?.growingWithOthers?.Content
                          }
                          overview={
                            tipsPersonalGrowthRelationshipsData
                              ?.growingWithOthers?.FlavorText
                          }
                          bgClr={"#e7feeb"} // Directly provide the encoded value
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={otherDimensionsData?.Heading}
                      color="yellow"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      <div className="flex flex-col h-full">
                        <DescriptiveCard
                          heading={
                            otherDimensionsData?.[`${otherDimensionsArray[0]}`]
                              ?.Heading
                          }
                          overview={
                            otherDimensionsData?.[`${otherDimensionsArray[0]}`]
                              ?.Content
                          }
                          bgClr="bg-orange-50"
                          textClr="text-orange-800"
                        />
                      </div>

                      <div className="flex flex-col h-full">
                        <DescriptiveCard
                          heading={
                            otherDimensionsData?.[`${otherDimensionsArray[1]}`]
                              ?.Heading
                          }
                          overview={
                            otherDimensionsData?.[`${otherDimensionsArray[1]}`]
                              ?.Content
                          }
                          bgClr="bg-emerald-50"
                          textClr="text-emerald-800"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                />
              ) : (
                <CallToActionSectionForUnAuth
                  CTA_1={CTA_1}
                  CTA_2={CTA_2}
                  onClick1={() => {
                    navigate(
                      `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                    );
                  }}
                  onClick2={() => {
                    navigate(
                      `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                    );
                  }}
                />
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getOrderDataByUserIdConnect={getOrderDataByUserIdConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </>
          )}
          <ReportWrapper
            assessmentId={assessmentId}
            assessmentAttemptId={assessmentAttemptId}
            currentLocale={currentLocale}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DiscPersonalityReport;
